import { useEffect, useState } from "react"
import { BarChart3, ChevronRight } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import CoverageHistogramPanel from "./CoverageHistogramPanel"

interface CoverageHistogramCardProps {
  /**
   * ISO3 country id of the selected data point, or null when nothing (or a
   * point without a country) is selected. The card renders nothing while this
   * is null, and auto-opens whenever it changes to a new country.
   */
  countryId: string | null
  countryName: string | null
  version: string
  getAccessToken: () => Promise<string>
  /** Active date-range start (year decimal), or null. Dims out-of-range years. */
  rangeStart: number | null
  /** Active date-range end (year decimal), or null. Dims out-of-range years. */
  rangeEnd: number | null
}

/**
 * CoverageHistogramCard renders the PR coverage histogram.
 * The card is shown only while a data point with a country is selected. It
 * auto-opens each time the selected country changes, while still letting the
 * user manually collapse or expand it for the current selection. The fetch,
 * loading, and error handling live in {@link CoverageHistogramPanel}.
 *
 * @param props - Component props.
 * @param props.countryId - ISO3 id of the selected point, or null when none.
 * @param props.countryName - Human-readable country name, or null when unknown.
 * @param props.version - Dataset version coverage is fetched for.
 * @param props.getAccessToken - Resolves an Auth0 access token for the request.
 * @param props.rangeStart - Active date-range start (year decimal), or null.
 * @param props.rangeEnd - Active date-range end (year decimal), or null.
 * @returns The collapsible coverage card, or null when no country is selected.
 */
export default function CoverageHistogramCard({
  countryId,
  countryName,
  version,
  getAccessToken,
  rangeStart,
  rangeEnd,
}: CoverageHistogramCardProps): React.JSX.Element | null {
  const [isOpen, setIsOpen] = useState<boolean>(true)

  // Re-open the card whenever a new country is selected, so a selection always
  // surfaces its coverage even if the user had collapsed the previous one.
  useEffect(() => {
    if (countryId != null) {
      setIsOpen(true)
    }
  }, [countryId])

  if (countryId == null) {
    return null
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      aria-label="PR coverage"
      className="overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md"
    >
      <CollapsibleTrigger className="group flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-muted/40">
        <BarChart3 className="size-3.5 text-muted-foreground -mt-0.5" aria-hidden="true" />
        <span className="flex items-baseline gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
          PR coverage
          {countryName != null && (
            <span className="font-medium normal-case tracking-normal text-muted-foreground">
              for {countryName}
            </span>
          )}
        </span>
        <ChevronRight className="ml-auto size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="border-t border-border/60 px-5 pb-4 pt-4">
          <CoverageHistogramPanel
            countryId={countryId}
            countryName={countryName}
            version={version}
            getAccessToken={getAccessToken}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
